import classnames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import NextImage from "next/image";
import SbEditable from "storyblok-react";
import { basicLoader } from "@/helpers/utils";
import { CaretDownIcon } from "@phosphor-icons/react";
import styles from "./faq-section.module.scss";
import type { FaqSectionStoryblok } from "./types";

interface Props {
  blok: FaqSectionStoryblok;
}

const FaqAnswer = ({
  open,
  answer,
  answerId,
  questionId,
}: {
  open: boolean;
  answer: string;
  answerId: string;
  questionId: string;
}) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const mounted = useRef(false);
  const [height, setHeight] = useState<number | "auto">(0);

  useEffect(() => {
    const inner = innerRef.current;
    if (!inner) return;

    // Reflect initial state without animating on first mount.
    if (!mounted.current) {
      mounted.current = true;
      setHeight(open ? "auto" : 0);
      return;
    }

    if (open) {
      // Animate from 0 to the measured content height, then release to auto
      // so the panel can reflow (e.g. on viewport resize) without clipping.
      setHeight(inner.scrollHeight);
      const id = window.setTimeout(() => setHeight("auto"), 300);
      return () => window.clearTimeout(id);
    }

    // Closing: pin the current height, then collapse on the next frame so the
    // transition has two definite endpoints to animate between.
    setHeight(inner.scrollHeight);
    const raf = window.requestAnimationFrame(() => setHeight(0));
    return () => window.cancelAnimationFrame(raf);
  }, [open]);

  return (
    <div
      id={answerId}
      className={classnames(styles.faqAnswer, {
        [styles.faqAnswerOpen]: open,
      })}
      role="region"
      aria-hidden={!open}
      aria-labelledby={questionId}
      style={{ height: height === "auto" ? "auto" : `${height}px` }}
      onTransitionEnd={(event) => {
        if (
          event.target !== event.currentTarget ||
          event.propertyName !== "height" ||
          !open
        ) {
          return;
        }

        setHeight("auto");
      }}
    >
      <div ref={innerRef} className={styles.faqAnswerInner}>
        <p>{answer}</p>
      </div>
    </div>
  );
};

const FaqSection = ({ blok }: Props) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const items = Array.isArray(blok.items) ? blok.items : [];

  const handleToggle = useCallback((index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  }, []);

  return (
    // @ts-expect-error
    <SbEditable content={blok}>
      <div className={styles.faq}>
        <div className={styles.faqContent}>
          {blok.heading ? (
            <h2 className={styles.faqHeading}>{blok.heading}</h2>
          ) : null}
          <div className={styles.faqAccordion}>
            {items.length
              ? items.map((item, index) => {
                  const questionId = `faq-question-${item._uid}`;
                  const answerId = `faq-answer-${item._uid}`;

                  return (
                    <div key={item._uid}>
                      {index > 0 ? <div className={styles.faqDivider} /> : null}
                      <div className={styles.faqItem}>
                        <button
                          id={questionId}
                          type="button"
                          className={styles.faqTrigger}
                          onClick={() => handleToggle(index)}
                          aria-expanded={openIndex === index}
                          aria-controls={item.answer ? answerId : undefined}
                        >
                          <span className={styles.faqQuestion}>
                            {item.question}
                          </span>
                          <span
                            className={classnames(styles.faqCaret, {
                              [styles.faqCaretOpen]: openIndex === index,
                            })}
                          >
                            <CaretDownIcon
                              color="currentColor"
                              size={24}
                              weight="bold"
                              aria-hidden="true"
                            />
                          </span>
                        </button>
                        {item.answer ? (
                          <FaqAnswer
                            open={openIndex === index}
                            answer={item.answer}
                            answerId={answerId}
                            questionId={questionId}
                          />
                        ) : null}
                      </div>
                    </div>
                  );
                })
              : null}
          </div>
        </div>
        {blok.image?.filename ? (
          <div className={styles.faqImage}>
            <NextImage
              loader={basicLoader}
              src={blok.image.filename}
              alt=""
              fill
              style={{ objectFit: "cover" }}
              sizes="(min-width: 1024px) 50vw, 100vw"
            />
          </div>
        ) : null}
      </div>
    </SbEditable>
  );
};

export default FaqSection;
